import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ArrowUpRight16 } from '@carbon/icons-react';
import { isEmpty, isNil } from 'lodash';
import { EntityType } from 'types';
import {
  selectEmailPreviewing,
  selectEmailPreview,
  previewResponseStart,
  clearEmailBodyDownload
} from 'redux/emails';
import { selectIsFromSwxLocate } from 'redux/session/selectors';
import {
  resetAttachmentPreviewUrl,
  resetAttachmentDownloadUrl
} from 'redux/enquiries/actions';
import {
  getEmailAttachment,
  isValidRegex,
  checkFileExtensionRegex
} from 'utilities/helper.util';
import { selectUser } from 'redux/user/selector';
import { fetchEnquiryShareLinkByIdService } from 'services/enquiry.service';
import { submitCollect } from 'redux/track/actions';
import { useComponentMount } from 'utilities/hooks.util';
import { InlineLoadingStatus } from 'carbon-components-react';
import DefaultView from './Default';
import { MobileGetPlans, MobileDownloadAndTools } from './Mobile';

export const GetPlans: React.FC<Props | any> = (props: Props) => {
  const {
    page,
    referrals,
    screenSize,
    handleBackButtonClick,
    goToTop,
    enquiryId,
    responsesExpired,
    showResponsesExpiredModal,
    creatorUserId,
    enquiryInformationRef,
    isModal,
    handleCLOOpenModal,
    isLoading,
    timestamp,
    enquiryDetail,
    setSelectedReferralExternalId,
    handleContactsSelection,
    shareId,
    onClickCloseResponseExpiredModal,
    selectedReferralOverview,
    setSelectedReferralOverview,
    setIsDownloadPdf,
    setIsDownloadZip,
    enquiryValidityDate,
    lualExtent,
    lualZoomLevel,
    lualMapZoomLevel,
    onLualMapZoomChange
  } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const loadingEmailPreview: any = useSelector(selectEmailPreviewing);
  const emailPreview: any = useSelector(selectEmailPreview);
  const user = useSelector(selectUser);
  const isFromSwxLocate = useSelector(selectIsFromSwxLocate);

  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [showDMFViewer, setShowDMFViewer] = useState<boolean>(false);
  const [responseReceivedText, setResponseReceivedText] = useState<string>('');
  const [allResponsesReceived, setAllResponsesReceived] = useState<boolean>(
    false
  );
  const [selectedReferral, setSelectedReferral] = useState<number>(-1);
  const [selectedReferralObj, setSelectedReferralObj] = useState<any>(null);
  const [selectedResponse, setSelectedResponse] = useState<number>(-1);
  const [selectedResponseObj, setSelectedResponseObj] = useState<any>(null);
  const [viewOrDownloadDropdownItem, setViewOrDownloadDropdownItem] = useState<
    Array<any>
  >([]);

  const [previewData, setPreviewData] = useState<any>(null);
  const [
    showPreviewerSuperModal,
    setShowPreviewerSuperModal
  ] = useState<boolean>(false);

  const [
    initialSelectedViewOrDownloadItem,
    setInitialSelectedViewOrDownloadItem
  ] = useState<any>(null);
  const [isLoadingPreviewer, setIsLoadingPreviewer] = useState<boolean>(false);
  const [
    copyPageLinkState,
    setCopyPageLinkState
  ] = useState<InlineLoadingStatus>('inactive');
  const [
    showOrganisationContactsModal,
    setShowOrganisationContactsModal
  ] = useState<boolean>(false);
  const [
    inboundEmailIdToDownload,
    setInboundEmailIdToDownload
  ] = useState<number>(0);
  const [showLualContent, setShowLualContent] = useState<boolean>(false);
  const [showLualSuperModal, setShowLualSuperModal] = useState<boolean>(false);
  const [selectionSource, setSelectionSource] = useState<
    'interactive-map' | 'responses'
  >('responses');

  const getPlanResponsesAboutRef: any = useRef(null);
  const prevSelectedReferralRef = useRef<number>(-1);
  const heroDefaultItemRef = useRef<any>(null);

  useComponentMount(() => {
    dispatch(clearEmailBodyDownload());
  });

  const handleResponseSelection = (
    index: number,
    source: 'interactive-map' | 'responses' = 'responses'
  ) => {
    const wasInteractiveMap = selectionSource === 'interactive-map';
    setSelectionSource(source);
    setSelectedReferral((prev) => {
      if (prev !== index) {
        setSelectedReferralObj(referrals[index]);
        setSelectedReferralExternalId(referrals[index]?.externalId);
        setSelectedResponse(-1);
        setPreviewData(null);
        setViewOrDownloadDropdownItem([]);
        setInitialSelectedViewOrDownloadItem(null);
        setInboundEmailIdToDownload(0);
        setSelectedReferralOverview(-1);
        dispatch(clearEmailBodyDownload());
        return index;
      }
      if (
        wasInteractiveMap &&
        source === 'responses' &&
        heroDefaultItemRef.current
      ) {
        handleViewOrDownloadDropdownChange(heroDefaultItemRef.current.value);
      }
      return prev;
    });
    setShowLualContent(false);
  };

  const handleResponseSelectionMobile = (index: number) => {
    setSelectionSource('responses');
    console.log('CLICK FROM RESPONSES');
    setSelectedReferral((prev) => {
      if (prev !== index) {
        setSelectedReferralObj(referrals[index]);

        // Used for contact handling (in parent component)
        // Not used in mobile view
        // setSelectedReferralExternalId(referrals[index]?.externalId);

        // Not used in mobile view
        setSelectedResponse(-1);

        // Will be used for attachment content
        setPreviewData(null);

        // Will be used for attachment list
        setViewOrDownloadDropdownItem([]);

        // Not used in mobile view
        // setInitialSelectedViewOrDownloadItem(null);

        setInboundEmailIdToDownload(0);

        setSelectedReferralOverview(-1);

        dispatch(clearEmailBodyDownload());
        return index;
      }
      return prev;
    });
  };

  const handleMultipleResponseSelection = (
    referralIndex: number,
    responseIndex: number,
    source: 'interactive-map' | 'responses' = 'responses'
  ) => {
    const wasInteractiveMap = selectionSource === 'interactive-map';
    setSelectionSource(source);
    setSelectedResponse((prev) => {
      if (prev !== responseIndex || selectedReferral !== referralIndex) {
        setSelectedReferral(referralIndex);
        setSelectedReferralObj(referrals[referralIndex]);
        setSelectedReferralExternalId(referrals[referralIndex]?.externalId);
        setSelectedResponseObj(
          referrals[referralIndex].responses[responseIndex]
        );
        setSelectedResponse(responseIndex);
        setPreviewData(null);
        setViewOrDownloadDropdownItem([]);
        setInitialSelectedViewOrDownloadItem(null);
        setTimeout(() => {
          setInboundEmailIdToDownload(
            referrals[referralIndex].responses[responseIndex]?.inboundEmailId
          );
        }, 100);
        setSelectedReferralOverview(-1);
        dispatch(clearEmailBodyDownload());
        return responseIndex;
      }
      if (
        wasInteractiveMap &&
        source === 'responses' &&
        heroDefaultItemRef.current
      ) {
        handleViewOrDownloadDropdownChange(heroDefaultItemRef.current.value);
      }
      return prev;
    });
    setShowLualContent(false);
  };

  const handleInteractiveMapSelection = (
    referralIndex: number,
    responseIndex: number
  ) => {
    const referral = referrals[referralIndex];
    const hasMultipleResponses =
      Array.isArray(referral?.responses) && referral.responses.length >= 2;
    if (hasMultipleResponses) {
      handleMultipleResponseSelection(
        referralIndex,
        responseIndex,
        'interactive-map'
      );
    } else {
      handleResponseSelection(referralIndex, 'interactive-map');
    }
  };

  const getSpatialDataLink = (responseObj: any) => {
    const links = responseObj?.custom_properties?.links;
    if (Array.isArray(links)) {
      const spatialLink = links.find((link: any) =>
        /spatial/i.test(link?.displayText || '')
      );
      if (spatialLink?.url) return spatialLink.url;
    }

    if (emailPreview?.body) {
      const match = emailPreview.body.match(
        /<a[^>]+href="([^"]+)"[^>]*>[^<]*Download spatial[^<]*<\/a>/i
      );
      if (match?.[1]) return match[1];
    }

    return '';
  };

  const handleDownloadSpatialDataClick = () => {
    const url = getSpatialDataLink(selectedResponseObj);
    if (!url) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.target = '_blank';
    anchor.click();
  };

  const handleViewResponseClick = () => {
    setSelectionSource('responses');
    console.log(
      '== initialSelectedViewOrDownloadItem',
      initialSelectedViewOrDownloadItem
    );
    if (heroDefaultItemRef.current) {
      handleViewOrDownloadDropdownChange(heroDefaultItemRef.current.value);
    }
  };

  const handleRenewEnquiry = useCallback(() => {
    if (enquiryId) {
      let url = `/enquiries/new?clone=${enquiryId}`;

      if (shareId) {
        url += `&shareId=${shareId}`;
      }

      history.push(url);
    }
  }, [shareId, enquiryId, history]);

  const handleCopyPageLink = async () => {
    dispatch(
      submitCollect({ payload: { enquiryId }, type: EntityType.COPY_LINK })
    );
    setCopyPageLinkState('active');

    if (typeof ClipboardItem && navigator.clipboard.write) {
      const text = new ClipboardItem({
        'text/plain': fetchEnquiryShareLinkByIdService(enquiryId).then(
          (response) => {
            try {
              if (!response.error) {
                setCopyPageLinkState('finished');
              } else {
                setCopyPageLinkState('error');
              }

              return new Blob([response], { type: 'text/plain' });
            } catch (e) {
              console.error(e);
              setCopyPageLinkState('error');
              return new Blob([''], { type: 'text/plain' });
            } finally {
              setTimeout(() => setCopyPageLinkState('inactive'), 2000);
            }
          }
        )
      });

      text && navigator.clipboard.write([text]);
    } else {
      try {
        const response = await fetchEnquiryShareLinkByIdService(enquiryId);

        if (!response.error) {
          navigator.clipboard.writeText(response);
          setCopyPageLinkState('finished');
        } else {
          setCopyPageLinkState('error');
        }
      } catch (e) {
        console.error(e);
        setCopyPageLinkState('error');
      } finally {
        setTimeout(() => setCopyPageLinkState('inactive'), 2000);
      }
    }
  };

  const resetAttachmentPreviewNDownload = useCallback(() => {
    dispatch(resetAttachmentPreviewUrl());
    dispatch(resetAttachmentDownloadUrl());
  }, [dispatch]);

  const supportAttachment = useCallback(
    (valueIndex: string) => {
      let id = '';
      let body = '';
      let type = '';
      let name = '';
      let isFileSupported = false;
      const filename = emailPreview.Attachments[Number(valueIndex)].name;
      const mimeType =
        emailPreview.Attachments[Number(valueIndex)].mimeType || 'unknown';

      // check for file extension as pdf and gml sometimes having same mimetype
      if (
        ['application/pdf', 'application/octet-stream'].includes(mimeType) &&
        checkFileExtensionRegex(filename, 'pdf')
      ) {
        isFileSupported = true;
        type = 'pdf';
      } else if (
        [
          'image/png',
          'image/jpeg',
          'image/gif',
          'image/bmp',
          'image/vnd.microsoft.icon',
          'image/svg+xml',
          'image/tiff'
        ].includes(mimeType)
      ) {
        isFileSupported = true;
        type = 'image';
      } else if (['model/vnd.dwf', 'drawing/x-dwf'].includes(mimeType)) {
        isFileSupported = !!emailPreview.isSupportDWF;
        type = 'dwf';
      } else {
        isFileSupported = false;
        type = 'unknown';
      }

      id = emailPreview.Attachments[Number(valueIndex)].id;
      body = emailPreview.Attachments[Number(valueIndex)].url;
      name = emailPreview.Attachments[Number(valueIndex)].name;
      const data = {
        id,
        body,
        type,
        name
      };

      return {
        data,
        isFileSupported
      };
    },
    [emailPreview]
  );

  const handleViewOrDownloadDropdownChange = useCallback(
    (value: string) => {
      setShowDMFViewer(false);
      setIsLoadingPreviewer(true);
      const selectedItem = viewOrDownloadDropdownItem?.find(
        (item: any) => item?.value === value
      );
      if (selectedItem) {
        setInitialSelectedViewOrDownloadItem(selectedItem);
      }
      let id = '';
      let body = '';
      let type = '';
      let name = '';
      let isFileSupported = false;

      if (value === 'webMap') {
        type = value;
        body = selectedResponseObj?.custom_properties?.webMapURL;
        isFileSupported = true;
      } else if (value === 'responseBody') {
        body = emailPreview.body;
        type = 'html';
        name = 'responseBody';
        isFileSupported = true;
      } else {
        const valueSplit = value.split('|');
        if (valueSplit[0] === 'attachment') {
          const attachmentData = supportAttachment(valueSplit[1]);
          id = attachmentData?.data.id;
          body = attachmentData?.data.body;
          name = attachmentData?.data.name;
          type = attachmentData?.data.type;
          isFileSupported = attachmentData?.isFileSupported;
        } else if (valueSplit[0] === 'link') {
          const anchor = document.createElement('a');
          anchor.href = valueSplit[1];
          anchor.target = '_blank';
          anchor.click();
          isFileSupported = false;
          body = valueSplit[1];
          type = 'link';
          name = valueSplit[2];
        }
      }

      const data = {
        id,
        body,
        type,
        name
      };

      dispatch(
        submitCollect({
          payload: { type, name, responseId: selectedResponseObj.responseId },
          type: EntityType.VIEW_ATTACHMENT
        })
      );

      if (isFileSupported) {
        resetAttachmentPreviewNDownload();
        setPreviewData(data);
      }

      if (!isFileSupported && type !== 'link') {
        getEmailAttachment({ url: body, attachmentName: name });
      }
      setTimeout(() => {
        setIsLoadingPreviewer(false);
      }, 100);
    },
    [
      selectedResponseObj,
      emailPreview,
      viewOrDownloadDropdownItem,
      dispatch,
      resetAttachmentPreviewNDownload,
      supportAttachment
    ]
  );

  const handleExpandClick = () => {
    setShowPreviewerSuperModal(!showPreviewerSuperModal);
  };

  const onClosePreviewerSuperModal = () => {
    setShowPreviewerSuperModal(false);
  };

  const handleContactsIconClick = () => {
    setShowOrganisationContactsModal(!showOrganisationContactsModal);
  };

  const handleShowLualContent = () => {
    dispatch(
      submitCollect({
        payload: { enquiryId, userId: user.id },
        type: EntityType.VIEW_LUAL
      })
    );

    setShowLualContent(true);
  };

  const handleOpenLual = () => {
    const { xmin, ymin, xmax, ymax, spatialReference } = lualExtent;
    const extentString = `${xmin},${ymin},${xmax},${ymax},${spatialReference.wkid}`;
    const lualUrl = `https://byda.maps.arcgis.com/apps/webappviewer/index.html?id=8a8f088ca9774464884d6711b347fff8&extent=${extentString}`;

    dispatch(
      submitCollect({
        payload: { enquiryId, userId: user.id },
        type: EntityType.OPEN_LUAL_LINK
      })
    );

    window.open(lualUrl, '_blank');
  };

  const handleExpandLual = () => {
    setShowLualSuperModal(!showLualSuperModal);
  };

  const onCloseLualSuperModal = () => {
    setShowLualSuperModal(false);
  };

  // Handles response view when referral response is opened from referral overview page
  useEffect(() => {
    if (selectedReferralOverview >= 0) {
      const referral = referrals[selectedReferralOverview];
      const referralResponsesLength =
        referral.status === 'COMPLETED' ? referral.responses.length : 0;

      setSelectedReferral(selectedReferralOverview);
      setSelectedReferralObj(referrals[selectedReferralOverview]);
      setSelectedReferralExternalId(
        referrals[selectedReferralOverview]?.externalId
      );
      setSelectedResponse(
        referralResponsesLength > 1 ? referralResponsesLength - 1 : -1
      );
      setPreviewData(null);
      setViewOrDownloadDropdownItem([]);
      setInitialSelectedViewOrDownloadItem(null);
      setInboundEmailIdToDownload(0);
      dispatch(clearEmailBodyDownload());
    }
  }, [
    selectedReferralOverview,
    dispatch,
    referrals,
    setSelectedReferralExternalId
  ]);

  const clearCacheAndUpdateResponses = useCallback(() => {
    if (referrals?.length > 0) {
      let completedReferral = 0;
      for (const referral of referrals) {
        if (referral.status === 'COMPLETED') {
          completedReferral += 1;
        }
      }
      const text = `${completedReferral} of ${referrals.length} ready`;
      setResponseReceivedText(text);
      if (completedReferral === referrals.length) {
        setAllResponsesReceived(true);
      } else {
        setAllResponsesReceived(false);
      }
    }
  }, [referrals]);

  useEffect(() => {
    clearCacheAndUpdateResponses();
  }, [clearCacheAndUpdateResponses]);

  useEffect(() => {
    if (user) {
      setIsOwner(creatorUserId === user.id);
    }
  }, [user, creatorUserId]);

  useEffect(() => {
    if (selectedReferral >= 0 && selectedReferralObj?.responses?.length > 0) {
      const { organisationId, responses } = selectedReferralObj;
      // set latest response as default
      const latestResponseObj = responses[0];

      // Only update when the actual response changed — avoids re-triggering
      // the preview fetch chain when referrals data refreshes (e.g. webhook)
      // but the selected response is still the same.
      if (latestResponseObj?.responseId !== selectedResponseObj?.responseId) {
        setSelectedResponseObj(latestResponseObj);
        setInboundEmailIdToDownload(latestResponseObj.inboundEmailId);
        dispatch(
          submitCollect({
            payload: {
              organisationId,
              responseId: latestResponseObj.responseId,
              enquiryId
            },
            type: EntityType.VIEW_AO_RESPONSE
          })
        );
      }
    } else {
      setSelectedResponseObj(null);
      setInboundEmailIdToDownload(0);
    }
    // eslint-disable-next-line
  }, [selectedReferral, selectedReferralObj, enquiryId, dispatch]);

  useEffect(() => {
    if (selectedResponse >= 0) {
      // set latest response as default
      setSelectedResponseObj(selectedReferralObj.responses[selectedResponse]);
    }
  }, [selectedResponse, selectedReferralObj]);

  useEffect(() => {
    if (selectedResponseObj?.responseId) {
      dispatch(
        previewResponseStart({
          responseId: selectedResponseObj.responseId,
          shareId,
          enquiryId: isFromSwxLocate ? enquiryId : undefined
        })
      );
    }
  }, [selectedResponseObj, dispatch, shareId, enquiryId, isFromSwxLocate]);

  const currentWebMapUrl = selectedResponseObj?.custom_properties?.webMapURL;
  const currentPreviewType = previewData?.type;

  /**
   * Ensures Interactive Map selections render the web map view.
   *
   * WHY: Must run in an effect because it reacts to selection changes and
   * preview state updates that occur after async response loading.
   */
  const ensureInteractiveMapView = useCallback(() => {
    if (
      selectionSource === 'interactive-map' &&
      currentWebMapUrl &&
      currentPreviewType !== 'webMap'
    ) {
      handleViewOrDownloadDropdownChange('webMap');
    }
  }, [
    selectionSource,
    currentWebMapUrl,
    currentPreviewType,
    handleViewOrDownloadDropdownChange
  ]);

  useEffect(() => {
    // Keep preview in web map mode when selection comes from Interactive map.
    ensureInteractiveMapView();
  }, [ensureInteractiveMapView]);

  useEffect(() => {
    if (
      !loadingEmailPreview &&
      emailPreview &&
      selectedReferralObj?.status === 'COMPLETED'
    ) {
      const items = [];
      let selectedItem = null;
      let hasWebMap = false;
      let defaultWebMap = true;
      let webMap = null;

      const emailBody = {
        label: 'Response body',
        value: 'responseBody'
      };

      if (selectedResponseObj?.custom_properties?.webMapURL) {
        webMap = { label: 'Web map', value: 'webMap' };
        items.push(webMap);
        hasWebMap = true;
      }

      if (selectedResponseObj?.custom_properties?.links?.length > 0) {
        for (const link of selectedResponseObj.custom_properties.links) {
          const linkObj = {
            label: (
              <div className="attachment-dropdown-items">
                <div>{link.displayText}</div>
                <div>
                  <ArrowUpRight16 />
                </div>
              </div>
            ),
            value: `link|${link.url}|${link.displayText}`
          };
          items.push(linkObj);
        }
      }

      items.push(emailBody);
      for (const [index, attachment] of emailPreview.Attachments.entries()) {
        if (attachment.emailContentDisposition !== 'inline') {
          const attachmentObj = {
            label: attachment.name,
            value: `attachment|${index}`
          };
          items.push(attachmentObj);
        }
      }

      if (selectedResponseObj?.primaryContent) {
        if (
          selectedResponseObj?.primaryContent.responseType ===
            'response_attachment' ||
          !isEmpty(selectedResponseObj.primaryContent.filenameOption)
        ) {
          const {
            filenameOption,
            filenameValue
          } = selectedResponseObj.primaryContent;

          // Create matcher function based on filename option
          const createMatcher = (option: string, value: string) => {
            const lowerValue = value.toLowerCase();

            switch (option) {
              case 'start_with':
                return (label: string) =>
                  label.toLowerCase().startsWith(lowerValue);
              case 'end_with':
                return (label: string) =>
                  label.toLowerCase().endsWith(lowerValue);
              case 'contains':
                return (label: string) =>
                  label.toLowerCase().includes(lowerValue);
              default:
                if (isValidRegex(value)) {
                  const regex = new RegExp(value);
                  return (label: string) => regex.test(label);
                }
                return () => false;
            }
          };

          const matcher = createMatcher(filenameOption, filenameValue);

          // Find and mark primary content item
          const filterItem = items
            // Assign an id to each item so we can access it to assign isPrimaryContent
            .map((item, index) => ({ ...item, id: index }))
            .filter(
              (item) => item.value !== 'webMap' && item.value !== 'responseBody'
            );

          selectedItem = filterItem.find((item) => {
            if (matcher(item.label)) {
              items[item.id] = { ...item, isPrimaryContent: true };
              return true;
            }
            return false;
          });
        }
        defaultWebMap = selectedResponseObj.primaryContent.webMap;
      }

      setViewOrDownloadDropdownItem(items);

      // Set the default view here
      // Set to Response Body until the AO settings page is done
      let data: any = { type: '', name: '' };
      if (hasWebMap && defaultWebMap) {
        data = {
          body: selectedResponseObj?.custom_properties?.webMapURL,
          type: 'webMap'
        };
      } else if (!isNil(selectedItem)) {
        const valueSplit = selectedItem.value.split('|');
        const support = supportAttachment(valueSplit[1]);
        if (support?.isFileSupported) {
          data = support?.data;
        }
      } else {
        data = {
          body: emailPreview.body,
          type: 'html',
          name: 'responseBody'
        };
      }

      if (selectedResponseObj?.responseId) {
        dispatch(
          submitCollect({
            payload: {
              type: data.type,
              name: data.name,
              responseId: selectedResponseObj.responseId
            },
            type: EntityType.VIEW_ATTACHMENT
          })
        );
      }

      const heroDefault =
        hasWebMap && defaultWebMap
          ? webMap
          : !isNil(selectedItem)
          ? selectedItem
          : emailBody;
      heroDefaultItemRef.current = heroDefault;
      setInitialSelectedViewOrDownloadItem(heroDefault);
      setPreviewData(data);
    }
    // eslint-disable-next-line
  }, [loadingEmailPreview, emailPreview]);

  // When referrals data changes (e.g. new webhook responses) but the user
  // hasn't changed their selection, only sync the referral object — do NOT
  // reset previewData so the currently viewed web map stays stable.
  useEffect(() => {
    if (selectedReferral >= 0) {
      const didSelectionChange =
        prevSelectedReferralRef.current !== selectedReferral;
      prevSelectedReferralRef.current = selectedReferral;

      setSelectedReferralObj(referrals[selectedReferral]);
      setSelectedReferralExternalId(referrals[selectedReferral]?.externalId);

      if (didSelectionChange) {
        setPreviewData(null);
        setViewOrDownloadDropdownItem([]);
        setInitialSelectedViewOrDownloadItem(null);
        setInboundEmailIdToDownload(0);
        dispatch(clearEmailBodyDownload());
      }
    }
  }, [selectedReferral, dispatch, referrals, setSelectedReferralExternalId]);

  const mobileViewGetPlansProps = {
    referrals,
    enquiryId,
    responsesExpired,
    showResponsesExpiredModal,
    selectedReferral,
    selectedReferralObj,
    selectedResponse,
    loadingEmailPreview,
    emailPreview,
    viewOrDownloadDropdownItem,
    handleViewOrDownloadDropdownChange,
    previewData,
    inboundEmailIdToDownload,
    handleResponseSelectionMobile,
    handleMultipleResponseSelection,
    onClickCloseResponseExpiredModal
  };

  const mobileViewDownloadAndToolsProps = {
    screenSize,
    enquiryId,
    responsesExpired,
    showResponsesExpiredModal,
    isModal,
    handleCLOOpenModal,
    isLoading,
    enquiryDetail,
    shareId,
    setIsDownloadPdf,
    setIsDownloadZip,
    lualExtent,
    isFromSwxLocate,

    selectedReferralObj,
    showDMFViewer,
    setShowDMFViewer,
    previewData,
    showPreviewerSuperModal,
    onClosePreviewerSuperModal,
    getPlanResponsesAboutRef,
    inboundEmailIdToDownload,
    handleOpenLual,
    handleExpandLual,
    showLualSuperModal,
    onCloseLualSuperModal,
    allResponsesReceived,
    copyPageLinkState,
    handleRenewEnquiry,
    handleCopyPageLink,
    isOwner,
    responseReceivedText,
    lualZoomLevel,
    lualMapZoomLevel,
    onLualMapZoomChange
  };

  const defaultViewProps = {
    referrals,
    screenSize,
    handleBackButtonClick,
    goToTop,
    enquiryId,
    responsesExpired,
    showResponsesExpiredModal,
    creatorUserId,
    enquiryInformationRef,
    isModal,
    handleCLOOpenModal,
    isLoading,
    timestamp,
    enquiryDetail,
    setSelectedReferralExternalId,
    handleContactsSelection,
    shareId,
    onClickCloseResponseExpiredModal,
    selectedReferralOverview,
    setSelectedReferralOverview,
    setIsDownloadPdf,
    setIsDownloadZip,
    enquiryValidityDate,
    lualExtent,
    isFromSwxLocate,
    lualZoomLevel,
    lualMapZoomLevel,
    onLualMapZoomChange,

    // State and handlers
    selectedReferral,
    selectedReferralObj,
    selectedResponse,
    showDMFViewer,
    setShowDMFViewer,
    loadingEmailPreview,
    emailPreview,
    viewOrDownloadDropdownItem,
    handleViewOrDownloadDropdownChange,
    previewData,
    handleExpandClick,
    initialSelectedViewOrDownloadItem,
    isLoadingPreviewer,
    handleContactsIconClick,
    getPlanResponsesAboutRef,
    inboundEmailIdToDownload,
    selectionSource,
    hasSpatialDataLink: !!getSpatialDataLink(selectedResponseObj),
    onDownloadSpatialDataClick: handleDownloadSpatialDataClick,
    onViewResponseClick: handleViewResponseClick,
    showPreviewerSuperModal,
    onClosePreviewerSuperModal,
    showOrganisationContactsModal,
    showLualContent,
    handleShowLualContent,
    handleOpenLual,
    handleExpandLual,
    showLualSuperModal,
    onCloseLualSuperModal,
    handleResponseSelection,
    handleResponseSelectionMobile,
    handleMultipleResponseSelection,
    handleInteractiveMapSelection,
    allResponsesReceived,
    copyPageLinkState,
    handleRenewEnquiry,
    handleCopyPageLink,
    isOwner,
    responseReceivedText
  };

  if (page === 'mobile-getPlans') {
    return <MobileGetPlans {...mobileViewGetPlansProps} />;
  }

  if (page === 'mobile-download-and-tools') {
    return <MobileDownloadAndTools {...mobileViewDownloadAndToolsProps} />;
  }

  return <DefaultView {...defaultViewProps} />;
};

type ownProps = {
  page: string;
  referrals: any;
  goToTop: Function;
  screenSize: String;
  handleBackButtonClick: Function;
  enquiryId: number;
  responsesExpired: boolean;
  showResponsesExpiredModal: boolean;
  creatorUserId: number;
  enquiryInformationRef: any;
  isModal: boolean;
  handleCLOOpenModal: any;
  isLoading: boolean;
  timestamp: Date;
  enquiryDetail: any;
  setSelectedReferralExternalId: Function;
  handleContactsSelection: Function;
  onClickClose: Function;
  shareId: string;
  onClickCloseResponseExpiredModal: Function;
  selectedReferralOverview: number;
  setSelectedReferralOverview: Function;
  setIsDownloadPdf: Function;
  setIsDownloadZip: Function;
  enquiryValidityDate: string;
  lualExtent: any;
  lualZoomLevel?: number | null;
  lualMapZoomLevel?: number | null;
  onLualMapZoomChange?: (zoomLevel: number) => void;
};

type Props = ownProps;

GetPlans.displayName = 'GetPlans';

export default React.memo(GetPlans);
